import { useRouterState } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { fetchWeddingDetail, weddingKeys } from "../features/weddings/weddings.api.js";
import { eventKeys, fetchEventDetail } from "../features/events/events.api.js";
import type { Role } from "@smartvow/shared";

export interface NavLink {
  to: string;
  params?: Record<string, string>;
  hash?: string;
  label: string;
  sub?: boolean;
}
export interface NavGroup {
  title: string;
  to?: string;
  params?: Record<string, string>;
  links: NavLink[];
}

const GLOBAL: Record<Role, NavLink[]> = {
  client: [
    { to: "/app", label: "Mon mariage" },
    { to: "/app/account", label: "Mon compte" },
  ],
  admin: [
    { to: "/admin", label: "Demandes de prix" },
    { to: "/admin/clients", label: "Comptes clients" },
    { to: "/admin/settings", label: "Réglages" },
    { to: "/app", label: "Mariages" },
    { to: "/app/account", label: "Mon compte" },
  ],
};

/**
 * Construit le menu à partir de la route courante. Réutilise le cache React Query
 * des écrans (mêmes clés) → aucune requête supplémentaire.
 */
export function useNavModel(role: Role): NavGroup[] {
  const path = useRouterState({ select: (s) => s.location.pathname });
  const weddingId = path.match(/^\/app\/weddings\/([^/]+)/)?.[1];
  const eventId = path.match(/^\/app\/events\/([^/]+)/)?.[1];

  const eventQ = useQuery({
    queryKey: eventKeys.detail(eventId ?? ""),
    queryFn: () => fetchEventDetail(eventId!),
    enabled: !!eventId,
  });
  const ctxWeddingId = weddingId ?? eventQ.data?.event.weddingId;
  const weddingQ = useQuery({
    queryKey: weddingKeys.detail(ctxWeddingId ?? ""),
    queryFn: () => fetchWeddingDetail(ctxWeddingId!),
    enabled: !!ctxWeddingId,
  });

  const groups: NavGroup[] = [{ title: "Navigation", links: GLOBAL[role] }];

  if (weddingQ.data) {
    const w = weddingQ.data.wedding;
    const p = { id: w.id };
    groups.push({
      title: w.title,
      to: "/app/weddings/$id",
      params: p,
      links: [
        { to: "/app/weddings/$id", params: p, hash: "couple", label: "Le couple" },
        { to: "/app/weddings/$id", params: p, hash: "photos", label: "Photos du couple" },
        { to: "/app/weddings/$id", params: p, hash: "events", label: "Événements" },
        ...weddingQ.data.events.map(
          (ev): NavLink => ({
            to: "/app/events/$id",
            params: { id: ev.id },
            label: ev.name,
            sub: true,
          }),
        ),
      ],
    });
  }

  if (eventQ.data) {
    const e = eventQ.data.event;
    const p = { id: e.id };
    groups.push({
      title: e.name,
      to: "/app/events/$id",
      params: p,
      links: [
        { to: "/app/events/$id", params: p, hash: "qr", label: "QR code invité" },
        { to: "/app/events/$id", params: p, hash: "theme", label: "Thème" },
        { to: "/app/events/$id", params: p, hash: "gallery", label: "Galerie" },
      ],
    });
  }

  return groups;
}
